import { useEffect, useState } from 'react';
import { apiBlobUrl } from '../lib/api';

/**
 * An image behind a permission-gated route.
 *
 * `<img src="/api/v1/…">` sends no Authorization header, and this API accepts
 * authorisation from nowhere else — so every such tag answered 401 and drew a
 * broken image. That is what the listing form has been doing with a landlord's
 * own photographs: the thumbnails in the editor never loaded, on any listing,
 * and nothing reported it because a failed <img> is silent.
 *
 * Fetching the bytes with the session token and rendering a blob: URL is the
 * right shape for a thumbnail (a couple of hundred kilobytes, wanted whole and
 * at once). It is the WRONG shape for video, which must stream and seek — see
 * the media ticket in domain/tokens.ts for that.
 *
 * The object URL is revoked when the component unmounts or the source changes;
 * a page of thumbnails that forgot to would hold every image it had ever shown.
 */
export function AuthedImage({ src, alt, className, loading }: {
  src: string; alt: string; className?: string; loading?: 'lazy' | 'eager';
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let revoked = false;
    let objectUrl: string | null = null;
    setUrl(null); setFailed(false);

    apiBlobUrl(src)
      .then((u) => {
        // The effect may have been torn down while the fetch was in flight;
        // revoking immediately keeps the leak from outliving the component.
        if (revoked) { URL.revokeObjectURL(u); return; }
        objectUrl = u;
        setUrl(u);
      })
      .catch(() => { if (!revoked) setFailed(true); });

    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  if (failed) return <span className={className} aria-label={`${alt} (could not be loaded)`} role="img" />;
  if (!url) return <span className={className} aria-hidden />;
  return <img src={url} alt={alt} className={className} loading={loading} />;
}
